import { darken } from 'polished';
import React, { ReactNode } from 'react';
import { styled } from 'styled-components';
import { pxInRem, tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { TextAlignment, TextSettings } from '../../canvas/entities/TextConventions';
import { ParticipantCounter } from '../../canvas/participants/Counter';
import { useParticipant } from '../../canvas/participants/ParticipantHooks';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PressableProps, RenderBounds, RenderBox, RenderButton, RenderEmoticon, RenderInner, RenderUrl, RenderUserInfo, useBracketShortcut, usePose, usePressable2 } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { Voter } from './Voter';
import { VoterLayout } from './VoterLayout';
import { VoterTitleFieldTextSettings } from './VoterTitleField';

const itemSettings = TextSettings.from({
    ...TextSettings.default,
    fontColor: 'inherit',
    fontSize: 0,
    maxFontSize: 64,
    textMargin: 10,
    textAlignH: TextAlignment.middle,
    textAlignV: TextAlignment.middle,
});


const RenderBorder: LFC<{ object: Voter }> = ({ object }) => {
    const { base } = object.layout;
    return <>
        <RenderBox
            {...base.toCSS()}
            background={Colors.white}
            borderRadius="6px"
            border={`solid 1px ${Colors.border}`}
            borderBottom={`solid 3px ${Colors.border}`}
        />
    </>;
};

type RenderItemButtonProps = PressableProps & { selected?: boolean, winner?: boolean, color?: string, children?: ReactNode };
const RenderItemButton = React.memo<RenderItemButtonProps>(({ area, state, selected, winner, color, children }) => {

    const border =
        (winner ? 'solid 4px gold' : null) ??
        (selected ? `solid 2px ${Colors.primary}` : null) ??
        (state === 'active' ? `solid 1px ${darken(.1, Colors.primary)}` : null) ??
        (state === 'hover' ? `solid 1px ${Colors.primary}` : null) ??
        `solid 2px ${tintSafe(.5, color) || Colors.border}`;

    const boxShadow =
        (winner ? '0 0 0 4px gold' : null) ??
        (selected ? `0 0 0 2px ${Colors.primary}` : null) ??
        (state === 'active' ? `0 0 0 1px ${darken(.1, Colors.primary)}` : null) ??
        (state === 'hover' ? `0 0 0 1px ${Colors.primary}` : null) ??
        null;

    const props = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    return <RenderBox {...area.toCSS()} {...props} color={color}>
        {children}
        <RenderBox inset="0" borderRadius="4px" {...{ border, boxShadow }} />
    </RenderBox>;
});

const RenderItem = ({ object, index }: { object: Voter, index: number }) => {

    const me = useResolve(ProfileModel);
    const item = object.items[index];
    useComponentObserver(item);

    const color = item.color || Colors.text;
    const pressable = usePressable2(object, `item${index}`);
    const selected = !!object.votes.find(x => x.itemId === item.id && x.userId === me.id);
    const winner = object.state === 'results' && !!object.winners.find(x => x.id === item.id);

    const bs = useBracketShortcut(item.text);

    const bounds = pressable.area.set({ left: 0, top: 0 });
    const size = Math.min(bounds.width, bounds.height);

    return <RenderItemButton {...{ color, selected, winner, ...pressable }}>
        {bs.type === 'none' && <RenderText bounds={bounds} settings={itemSettings} value={item.text} />}
        {bs.type === 'highlight' && <RenderTextHighlight color={color}><RenderText bounds={bounds} settings={itemSettings} value={bs.value} /></RenderTextHighlight>}
        {bs.type === 'emoticon' && <RenderEmoticon value={bs.value} size={`${size}px`} />}
        {bs.type === 'user' && <RenderUserInfo value={bs.value} />}
        {bs.type === 'url' && <RenderUrl value={bs.value} />}
    </RenderItemButton>;
};

const RenderTextHighlight = styled.div<{ color: string }>`
    span {
        background: ${p => p.color};
        color: ${p => Colors.contrastTextColorFor(p.color)} !important;
        padding: 2px 4px;
        border-radius: 4px;
    }
`;

const RenderVote = React.memo<{ area: Rect, userId: string }>(({ area, userId }) => {
    const ptpt = useParticipant(userId);
    const transition = `
        left 200ms ease-out,
        top 200ms linear
    `;

    return <RenderBox {...area.toCSS()} {...{ transition }}>
        <ParticipantCounter info={ptpt} size={pxInRem(area.width)} />
    </RenderBox>;
});

const RenderVotes = React.memo<{ layout: VoterLayout }>(({ layout }) => {
    return <>
        {layout.votes.map(x => <RenderVote key={x.userId} {...x} />)}
    </>;
});

export const VoterVisual: LFC<{ object: Voter }> = ({ object, children }) => {

    const revealReset = usePressable2(object, 'revealReset');
    const { beingEdited } = usePose(object);
    const hasVotes = object.layout.votes.length > 0;

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder object={object} />
                {!beingEdited && <RenderText bounds={object.layout.title} settings={VoterTitleFieldTextSettings(object)} value={object.title} />}
                {object.items.map((x, i) => <RenderItem key={x.id} object={object} index={i} />)}
                <RenderVotes layout={object.layout} />
                {hasVotes && <RenderButton {...revealReset}>
                    {object.state === 'voting' ? 'Reveal Votes' : 'Clear Votes'}
                </RenderButton>}
            </RenderInner>
        </RenderBounds>
    </>;
};
